import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { httpResource } from '@angular/common/http';
import { OrganizationService } from '../../Modules/Organizations/Services/organization.service';
import { OrganizationNoteService } from '../../Modules/Organizations/Services/organization-note.service';
import { PlanService } from '../../Modules/Plans/Services/plan.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { OperatorFacade } from '../operator.facade';
import {
  ROLE_EDIT_ORGANIZATION,
  ROLE_LIFECYCLE,
  ROLE_NOTES,
} from '../../Consts/organization-detail.const';
import { OrganizationStatus } from '../../Enums/organization-status.enum';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { OrganizationEditPayload } from '../../Modules/Organizations/Interfaces/organization-edit.interface';
import { Market } from '../../Models/backoffice/market.model';
import { OrganizationDetail } from '../../Models/backoffice/organization-detail.model';
import { OrganizationNote } from '../../Models/backoffice/organization-note.model';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { MarketListResponse } from '../../Interfaces/api/backoffice/market.interface';
import { OrganizationShowResponse } from '../../Interfaces/api/backoffice/organization.interface';
import { OrganizationNoteListResponse } from '../../Interfaces/api/backoffice/organization-note.interface';

@Injectable()
export class OrganizationDetailFacade {
  private readonly _organizationService = inject(OrganizationService);
  private readonly _noteService = inject(OrganizationNoteService);
  private readonly _planService = inject(PlanService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _organizationId = signal<string>('');
  private readonly _isMutating = signal<boolean>(false);

  private readonly _organization = httpResource<LaravelDefaultResponse<OrganizationShowResponse>>(
    () =>
      this._organizationId()
        ? this._organizationService.showRequest(this._organizationId())
        : undefined,
  );

  private readonly _notes = httpResource<LaravelDefaultResponse<OrganizationNoteListResponse>>(() =>
    this._organizationId() ? this._noteService.listRequest(this._organizationId()) : undefined,
  );

  private readonly _markets = httpResource<LaravelDefaultResponse<MarketListResponse>>(() =>
    this._planService.marketsRequest(),
  );

  readonly organizationId = computed<string>(() => this._organizationId());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly organization = computed<OrganizationDetail | null>(() =>
    this._organizationService.toDetail(this._resourceState.valueOf(this._organization)),
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._organization.status()),
  );

  readonly notes = computed<OrganizationNote[]>(() =>
    this._noteService.toNotes(this._resourceState.valueOf(this._notes)),
  );

  readonly notesState = computed<MetricState>(() =>
    this._resourceState.metricState(this._notes.status()),
  );

  readonly markets = computed<Market[]>(() => this._planService.toMarkets(this._resourceState.valueOf(this._markets)));

  readonly canEdit = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_EDIT_ORGANIZATION));

  readonly canChangeLifecycle = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_LIFECYCLE));

  readonly canWriteNotes = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_NOTES));

  readonly operatorId = computed<string>(() => this._operatorFacade.operator()?.id ?? '');

  readonly isSuspended = computed<boolean>(
    () => this.organization()?.status === OrganizationStatus.SUSPENDED,
  );

  readonly isTerminal = computed<boolean>(
    () => this.organization()?.status === OrganizationStatus.CANCELLED,
  );

  load(organizationId: string): void {
    this._organizationId.set(organizationId);
  }

  reload(): void {
    this._organization.reload();
    this._notes.reload();
  }

  save(payload: OrganizationEditPayload): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._organizationService
      .update(organizationId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterOrganizationWrite('backoffice.organizations.edit.saved'),
        error: (error) => this._failMutation(error),
      });
  }

  saveBillingEmail(billingEmail: string | null): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._organizationService
      .updateBillingEmail(organizationId, billingEmail)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterOrganizationWrite('backoffice.organizations.overview.billing_email.updated'),
        error: (error) => this._failMutation(error),
      });
  }

  suspend(reason: string): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._organizationService
      .suspend(organizationId, reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterOrganizationWrite('backoffice.organizations.overview.lifecycle.suspended'),
        error: (error) => this._failMutation(error),
      });
  }

  reactivate(): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._organizationService
      .reactivate(organizationId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterOrganizationWrite('backoffice.organizations.overview.lifecycle.reactivated'),
        error: (error) => this._failMutation(error),
      });
  }

  createNote(body: string, pinned: boolean): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._noteService
      .create(organizationId, body, pinned)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterNoteWrite('backoffice.organizations.notes.created'),
        error: (error) => this._failMutation(error),
      });
  }

  updateNote(noteId: string, body: string, pinned: boolean): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._noteService
      .update(organizationId, noteId, body, pinned)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterNoteWrite('backoffice.organizations.notes.updated'),
        error: (error) => this._failMutation(error),
      });
  }

  togglePinned(note: OrganizationNote): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._noteService
      .setPinned(organizationId, note.id, !note.pinned)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterNoteWrite('backoffice.organizations.notes.updated'),
        error: (error) => this._failMutation(error),
      });
  }

  deleteNote(noteId: string): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._noteService
      .remove(organizationId, noteId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterNoteWrite('backoffice.organizations.notes.deleted'),
        error: (error) => this._failMutation(error),
      });
  }

  canManageNote(note: OrganizationNote): boolean {
    return note.isAuthoredBy(this.operatorId()) || this._operatorFacade.atLeast(ROLE_LIFECYCLE);
  }

  private _afterOrganizationWrite(message: string): void {
    this._isMutating.set(false);
    this._organization.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _afterNoteWrite(message: string): void {
    this._isMutating.set(false);
    this._notes.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._errorHandler.messageFor(error), { type: 'error' });
  }
}
